import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import Loader from '@/components/ui/loader';
import { RequestDemoPhoneAndReasonFields } from '@/features/onboarding-v2/components/personalization/shared/RequestDemoPhoneAndReasonFields';
import { isOnPrem } from '@/utils/buildProfile';
import { submitSdrDemoLead } from '@/utils/sdrDemoLead';
import { getUtmParams } from '@/utils/utmTracking';
import { Button } from '@heidi/design-system/button';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { ArrowLeft } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isValidPhoneNumber, type Country } from 'react-phone-number-input';

import { deriveNameFromEmail } from './book-demo-calendar-step.utils';
import { DemoCountryLanguageFields } from './demo-country-language-fields';

interface BookDemoRequestDemoVariantProps {
  email: string;
  country: CountryType;
  onCountryChange: (country: CountryType) => void;
  languageCode: string | null;
  onLanguageChange: (language: string) => void;
  showLanguage: boolean;
  availableLanguages: LanguageCode[];
  onSubmitted: () => void;
  onBack: () => void;
  onTrackSubmitted: (countryName: string | undefined) => void;
}

export function BookDemoRequestDemoVariant({
  email,
  country,
  onCountryChange,
  languageCode,
  onLanguageChange,
  showLanguage,
  availableLanguages,
  onSubmitted,
  onBack,
  onTrackSubmitted,
}: BookDemoRequestDemoVariantProps) {
  const intl = useIntl();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [demoReason, setDemoReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const isValid =
    isValidPhoneNumber(phoneNumber) && demoReason.trim().length > 0 && !!email.trim();

  const handleSubmit = useCallback(async () => {
    if (!isValid || isSubmitting) return;

    // ON-PREM-GATE: belt-and-braces per F1 — country-config is PostHog-gated
    // (PostHog doesn't initialize on-prem), but an explicit check here ensures
    // a leaked PostHog key can never trigger a HubSpot lead submission from an
    // airgapped deployment.
    if (isOnPrem) {
      onSubmitted();
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);
    onTrackSubmitted(country.name);
    const { firstName, lastName } = deriveNameFromEmail(email);
    try {
      const ok = await submitSdrDemoLead({
        firstName,
        lastName,
        email,
        country: country.name,
        countryCode: country.countryCode,
        phone: phoneNumber || undefined,
        demoReason: demoReason.trim(),
        utmParams: getUtmParams(),
      });
      if (ok) {
        onSubmitted();
      } else {
        setSubmitError(true);
      }
    } finally {
      // Reset on success and failure so a thrown error doesn't strand the
      // submit button in a permanently-disabled state.
      setIsSubmitting(false);
    }
  }, [
    isValid,
    isSubmitting,
    onSubmitted,
    onTrackSubmitted,
    country.name,
    country.countryCode,
    email,
    phoneNumber,
    demoReason,
  ]);

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt="Heidi"
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage id="6+NbDkdsvI" defaultMessage="Book a demo" />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              id="3HTfryuB5j"
              defaultMessage="Tell us a bit about you and our team will be in touch."
            />
          </TypographyP2>
        </div>
      </div>

      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        <DemoCountryLanguageFields
          country={country}
          onCountryChange={onCountryChange}
          languageCode={languageCode}
          onLanguageChange={onLanguageChange}
          showLanguage={showLanguage}
          availableLanguages={availableLanguages}
        />

        <RequestDemoPhoneAndReasonFields
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          defaultCountry={country.countryCode as Country}
          demoReason={demoReason}
          onDemoReasonChange={setDemoReason}
          country={country.name}
          language={languageCode ?? intl.locale}
        />

        {submitError && (
          <TypographyP2
            role="alert"
            aria-live="polite"
            className="text-center text-validation-destructive"
            data-testid="demo-request-submit-error"
          >
            <FormattedMessage
              id="W/5hwrn09m"
              defaultMessage="Something went wrong. Please try again."
            />
          </TypographyP2>
        )}

        <Button
          size="lg"
          disabled={!isValid || isSubmitting}
          onClick={() => void handleSubmit()}
          data-testid="demo-request-submit-button"
        >
          {isSubmitting ? (
            <Loader className="size-5 text-text-primary-invert" />
          ) : (
            <FormattedMessage id="wSZR47Y5kj" defaultMessage="Submit" />
          )}
        </Button>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-1.5 text-text-secondary"
      >
        <ArrowLeft size={16} className="rtl:-scale-x-100" />
        <FormattedMessage id="cyR7KhiuaU" defaultMessage="Back" />
      </Button>
    </div>
  );
}
